import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X } from "lucide-react";
import { FaRegPaperPlane } from "react-icons/fa";
import axios from "axios";
import chatIconGif from "../../assets/chatboticon.gif";
import { useNavigate } from "react-router-dom";

export default function ChatBot() {
  const chatAgentName = sessionStorage.getItem("appName");
  const navigate = useNavigate();
  const admin_auto_id = sessionStorage.getItem("admin_auto_id");
  const app_type_id = sessionStorage.getItem("app_type_id");
  const customer_auto_id = sessionStorage.getItem("accessToken");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [themeColor, setThemeColor] = useState("#000000")
  const [themeIconColor, setThemeIconColor] = useState("#000000");
  const [webFont, setWebFont] = useState("");

  useEffect(() => {
    const storedStyle = sessionStorage.getItem("UIStyle");
    if (storedStyle) {
      const parsedStyle = JSON.parse(storedStyle);
      setThemeColor(parsedStyle.web_theme_colour);
      setThemeIconColor(parsedStyle.web_theme_icon_color);
      setWebFont(parsedStyle.web_font);
    }
  }, []);


  function lightenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + 255 * percent;
    let g = ((num >> 8) & 0x00ff) + 255 * percent;
    let b = (num & 0x0000ff) + 255 * percent;

    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));

    return `rgb(${r}, ${g}, ${b})`;
  }

  function darkenHexColor(hex, percent) {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || hex.length !== 7) {
      return 'rgb(0, 0, 0)'; // Fallback to black if invalid
    }

    const num = parseInt(hex.replace('#', ''), 16);
    let r = (num >> 16) - 255 * percent;
    let g = ((num >> 8) & 0x00ff) - 255 * percent;
    let b = (num & 0x0000ff) - 255 * percent;

    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));

    return `rgb(${r}, ${g}, ${b})`;
  }

  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: `Hi! This is ${chatAgentName} AI Assistant. How can I help you today?`,
    },
  ]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };


  const sendMessage = async () => {
    if (!input.trim()) return;

    const userQuery = input;
    setMessages(prev => [...prev, { sender: "user", text: userQuery }]);
    setInput("");
    setLoading(true);

    try {
      const geminiRes = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/chatbot-gemini`,
        { message: userQuery }
      );

      const intent = geminiRes.data.intent;
      const aiText = geminiRes.data.message;
      const accessToken = sessionStorage.getItem("accessToken");

      const showLoginPrompt = () => {
        setMessages(prev => [
          ...prev,
          {
            sender: "bot",
            text: "Please log in to access this section"
          }
        ]);
      };
 

      switch (intent) {
        case "faq":
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/faq");
          break;

        case "terms":
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/termsandcondition");
          break;

        case "privacy":
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/privacyandpolicy");
          break;

        case "contact":
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/contact");
          break;

        case "cart":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/cart");
          break;



        case "profile":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          if (accessToken) navigate("/dashboard", { state: { page: "profile" } });
          break;

        case "myorders":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          if (accessToken) navigate("/dashboard", { state: { page: "myorders" } });
          break;

        case "mywishlist":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          navigate("/dashboard", { state: { page: "mywishlist" } });
          break;


        case "mywallet":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          if (accessToken) navigate("/dashboard", { state: { page: "mywallet" } });
          break;

        case "myaddress":
          if (!accessToken) return showLoginPrompt();
          setMessages(prev => [...prev, { sender: "bot", text: aiText }]);
          if (accessToken) navigate("/dashboard", { state: { page: "myaddress" } });
          break;

        case "product": {
          const aiRes = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/ai_response`,
            {
              message: userQuery,
              admin_auto_id,
              app_type_id,
              customer_auto_id
            }
          );

          const reply = aiRes.data?.reply?.trim();
          const products = aiRes.data?.products ?? [];

          if (reply || products.length > 0) {


            setMessages(prev => [
              ...prev,
              {
                sender: "bot",
                text: reply || "Here are some products you may like:",
                products
              }
            ]);
          } else {
            setMessages(prev => [
              ...prev,
              { sender: "bot", text: "Sorry, I couldn't find product-related info." }
            ]);
          }

          break;
        }



        default:
          if (aiText)
            setMessages(prev => [...prev, { sender: "bot", text: aiText }]);

      }

    } catch (e) {
      setMessages(prev => [...prev, { sender: "bot", text: "Assistant unavailable." }]);
    } finally {
      setLoading(false);
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  return (
    <div
      className={`fixed z-50 flex flex-col items-end right-4 
    ${isOpen ? "bottom-[65px]" : "bottom-[64px] md:bottom-[32px]"} 
    ${webFont}`}
    >
      {/* Chat Window */}
      <div
        className={`bg-white border shadow-xl rounded-lg flex flex-col overflow-hidden
      transition-all duration-500 ease-in-out
      ${isOpen ? "w-[350px] h-[600px] opacity-100" : "w-[50px] h-0 opacity-0"}`}
        style={{ boxShadow: "0 10px 25px rgb(0 0 0 / 0.2)" }}
      >

        {/* Chat Header */}
        <div className="flex items-center justify-between p-3 font-bold text-white "
          style={{ backgroundColor: themeColor }}>
          <span>{chatAgentName} Chat Assistant</span>
          <button
            onClick={toggleChat}
            aria-label="Close chat"
            className="transition-colors duration-200 hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 p-3 space-y-2 overflow-y-auto">
          {messages.map((msg, idx) => (
            <div key={idx}>
              <div
                className={`max-w-[80%] p-2 rounded text-sm ${msg.sender === "user"
                  ? "ml-auto  text-black"
                  : "mr-auto "
                  }`}
                style={{ backgroundColor: msg.sender === "user" ? lightenHexColor(themeColor, 0.15) : darkenHexColor(themeColor, 0.1) }}

              >
                {msg.text}
              </div>

              {msg.sender === "bot" && msg.products?.length > 0 && (
                <div className="flex gap-4 p-2 overflow-x-auto">
                  {msg.products.map((product) => (
                    <div
                      key={product._id}
                      className="min-w-[150px] bg-white border rounded-lg shadow-md p-2"
                      onClick={() =>
                        navigate(`/product-detail/${product.product_auto_id}`)
                      }
                    >
                      <img
                        src={`https://grobizapp-n.s3.ap-south-1.amazonaws.com/products_images/${product.product_image}`}
                        alt={product.product_name}
                        className="object-cover w-full h-32 rounded cursor-pointer"
                      />
                      <div className="mt-2 text-sm font-medium truncate">
                        {product.product_name}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-blue-400">
                        {product.currency} {product.product_price}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
          {loading && (
            <div className="mr-auto flex items-center space-x-2 bg-gray-200 p-2 rounded text-sm max-w-[80%] animate-pulse">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <span className="w-2 h-2 delay-100 bg-gray-400 rounded-full animate-bounce" />
              <span className="w-2 h-2 delay-200 bg-gray-400 rounded-full animate-bounce" />
              <span>Typing...</span>
            </div>
          )}
        </div>

        {/* Input Field */}
        <div className="flex items-center p-2 border-t">
          <input
            className="flex-1 px-2 py-1 text-sm border rounded"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."

          />
          <button
            onClick={sendMessage}
            className="p-2 ml-2 text-white transition-transform duration-200 transform rounded-full hover:scale-110 active:scale-95"
            style={{ backgroundColor: themeColor }}
            onMouseEnter={(e) => {
              const darkenedColor = darkenHexColor(themeColor, 0.15); // darken by 15%
              e.currentTarget.style.backgroundColor = darkenedColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = themeColor;
            }}

            aria-label="Send message"
          >
            <FaRegPaperPlane className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="p-2 text-white transition-transform duration-300 transform rounded-full shadow-lg hover:scale-110 active:scale-95
               fixed right-4 bottom-16 md:bottom-8 z-[999]"
          style={{ backgroundColor: themeColor }}
          onMouseEnter={(e) => {
            const darkenedColor = darkenHexColor(themeColor, 0.15);
            e.currentTarget.style.backgroundColor = darkenedColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = themeColor;
          }}
          aria-label="Open chat"
        >
          <img src={chatIconGif} alt="Chat icon" className="w-8 h-8" />
        </button>
      )}

    </div>
  );
}
